/**
 * How the adopt surface PRESENTS an account: its label, and which row the drawer highlights.
 *
 * Its own file rather than extra exports beside the components, so those modules export components
 * only (the fast-refresh rule) and both rules stay testable without rendering anything.
 */
import type { RsAccountOverview } from './useAdoptAccounts';

/**
 * The bounded refusal an adopt came back with, or null.
 *
 * Read off `data.reason` and never off the prose: the reason is the contract, the message
 * is copy, and a client that branches on copy breaks on an edit. Shared by the drawer and the accounts
 * list so the two cannot disagree about where the reason lives.
 */
export function adoptRefusal(
  error: unknown,
): { reason: string; coordinate?: string; existingConnectionId?: string; existingPaused?: boolean } | null {
  if (typeof error !== 'object' || error === null || !('data' in error)) return null;
  const data = (
    error as {
      data?: { reason?: unknown; coordinate?: unknown; existingConnectionId?: unknown; existingPaused?: unknown };
    }
  ).data;
  if (typeof data?.reason !== 'string') return null;
  return {
    reason: data.reason,
    ...(typeof data.coordinate === 'string' ? { coordinate: data.coordinate } : {}),
    ...(typeof data.existingConnectionId === 'string' ? { existingConnectionId: data.existingConnectionId } : {}),
    // Absent — the row could not be read under a concurrent insert — falls through to the live-card
    // wording, which is the common case and the one that matches an unpaused blocker.
    ...(typeof data.existingPaused === 'boolean' ? { existingPaused: data.existingPaused } : {}),
  };
}

/**
 * Ranking wanted the single in-use account auto-selected, which would have covered 82% of
 * ambiguous groups with no human decision. But an owner or admin has to be the one who changes the
 * account, and `rs_account_adopted_by` has to be able to claim a human chose it, so the
 * pre-selection is a RANKING affordance and the primary button names the account it would bind.
 *
 * **Degrades to no highlight rather than guessing.** `usageCount` is null for every real workspace
 * today — the counts the ranking needs are platform-side and the account list does not carry
 * them — so this returns null until they exist. A highlight drawn from an absent ranking would be a
 * guess presented as a recommendation.
 */
export function highlightedAccountId(candidates: readonly RsAccountOverview[]): string | null {
  const inUse = candidates.filter((a) => typeof a.usageCount === 'number' && a.usageCount > 0);
  return inUse.length === 1 ? (inUse[0]?.accountId ?? null) : null;
}

/** What an account with no name of its own is called when it names no coordinates either. */
export const UNNAMED_ACCOUNT = 'Unnamed account';

/**
 * The account's own name, or null when it has none a person chose: unnamed, or the name the mirror minted.
 *
 * A minted name is `lookout-<workspaceId>-<connectionKey>` (`rs-account-mirror.ts`). It holds a workspace id and a
 * connection key, so it never reaches the screen (ACT2-1124: no ids in the UI). **Gated on `nameIsMinted`, NOT on
 * `lookoutOwned`**: the `lookout:` mark is also written by the platform create form, where the customer types the
 * name themselves, and a minted account may be renamed in RudderStack afterwards. Both of those keep their name. The
 * server answers `nameIsMinted` (`warehouse-adopt.ts`), so this cannot drift into a second spelling of the convention.
 */
function humanName(account: Pick<RsAccountOverview, 'name' | 'nameIsMinted'>): string | null {
  const name = account.name?.trim();
  return name === undefined || name === '' || account.nameIsMinted ? null : name;
}

/**
 * The label for an account: its own name, else its coordinates, else {@link UNNAMED_ACCOUNT}. Never an id: an id
 * identifies nothing a person recognises.
 */
export function accountDisplayName(
  account: Pick<RsAccountOverview, 'name' | 'nameIsMinted' | 'host' | 'database'>,
): string {
  return humanName(account) ?? (coordinatesOf(account) || UNNAMED_ACCOUNT);
}

/**
 * What a row CALLS itself, and the quieter line underneath it.
 *
 * An account with a name of its own keeps it as the title, with its coordinates underneath. An unnamed account, or one
 * with a minted name, takes its coordinates as the title (what the customer would call that warehouse) and has no
 * second line.
 */
export function accountRowIdentity(account: Pick<RsAccountOverview, 'name' | 'nameIsMinted' | 'host' | 'database'>): {
  title: string;
  detail: string | null;
} {
  const name = humanName(account);
  const coordinates = coordinatesOf(account);
  if (name === null) return { title: coordinates || UNNAMED_ACCOUNT, detail: null };
  return { title: name, detail: coordinates === '' ? null : coordinates };
}

/**
 * What an action on this row is CALLED to a screen reader: the visible title, so the accessible name starts with what
 * is on screen. Two unnamed accounts on one host can share it (Redshift and Databricks report no readable database).
 * The minted name would tell them apart, but it holds ids, so it is never read out (ACT2-1124).
 */
export function accountActionName(account: RsAccountOverview): string {
  return accountRowIdentity(account).title;
}

/** The account's coordinates as one string, or '' when the account names none. */
export function coordinatesOf(account: Pick<RsAccountOverview, 'host' | 'database'>): string {
  return [account.host, account.database].filter((v) => v !== null && v !== '').join(' / ');
}
